import { withNamespaces } from 'react-i18next';
import { connect } from 'react-redux';
import { useEffect, useState, useImperativeHandle } from 'react';
import { Row, Col, Label, Button, FormFeedback, Alert, Spinner } from "reactstrap";
import MaterialInput from "@material-ui/core/Input";
import InputMask from "react-input-mask";
import { useMutation } from '@tanstack/react-query';
import FollowUpForm from 'src/modules/data-protection/components/FollowUpForm';
import axios from 'axios';
import { API_BASE_URL } from 'src/modules/data-protection/constants';
import { toast } from 'react-toastify';
import { AvForm } from 'availity-reactstrap-validation';
import { authCurrentUser } from 'src/store/actions';
import ValidatableEmail from 'src/pages/Admin/administration/user-management/components/validatable-email';

const UpdateEmail = ({
    t,
    token,
    onBackButtonClicked,
    onUpdateComplete
}) => {
    const {
        mutate: updateUserEmail,
        isLoading: emailIsUpdating
    } = useMutation({
        mutationKey: ["data-protection-update-profile-email"],
        mutationFn: async (payload) => {
            return await axios.post(`${API_BASE_URL}/user/edit_email`, payload, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });
        },
        onSuccess: () => {
            onUpdateComplete();
        },
        onError: () => {
            toast(t("An error occurred while updating the email address."), {
                type: "error"
            });
        }
    });

    return (
        <AvForm
            className="needs-validation"
            onValidSubmit={(e, values) => updateUserEmail({
                email: values.email
            })}
        >
            <Row>
                <Col sm="12" md="6" lg="4">
                    <h5>{t("Follow-Up")}</h5>
                    <p>
                        {t(
                            "Please enter your email address so we can send you a one-time verification code (OTP) to proceed with your DSR request."
                        )}
                    </p>
                </Col>

                <Col sm="12" md="6" lg="8" className="mb-3">
                    <Label className="form-label" htmlFor="email">
                        {t("Email")}
                    </Label>

                    <ValidatableEmail 
                        index={0}
                        name='email'
                    />
                </Col>
            </Row>

            <Row>
                <Col sm="12" className="d-flex justify-content-between align-items-center">
                    <Button
                        onClick={() => {
                            if (onBackButtonClicked) {
                                onBackButtonClicked();
                            }
                        }}
                        color="btn btn-outline-light"
                        type="button"
                        disabled={emailIsUpdating}
                    >
                        {t("Back")}
                    </Button>

                    <Button
                        color="btn btn-primary"
                        type="submit"
                        disabled={emailIsUpdating}
                    >
                        {emailIsUpdating && (
                            <Spinner 
                                size='sm' 
                                color='white' 
                                className='me-1'
                            />
                        )}
                        {t('Next')}
                    </Button>
                </Col>
            </Row>
        </AvForm>
    )
}

const FollowUp = ({
    t,
    isActiveTab,

    otpError,
    
    actionsRef,
    user,
    token,
    onUserFilledOtpInput,
    onBackButtonClicked,
    onDsrFollowUpDetailsChange,

    authCurrentUser
}) => {
    const enableOtpResendIn = 60;

    const [username, setUsername] = useState();
    const [password, setPassword] = useState();
    const [otp, setOtp] = useState();
    const [otpSent, setOtpSent] = useState(false);
    const [otpCountdown, setOtpCountdown] = useState(enableOtpResendIn);
    const [resendInterval, setResendInterval] = useState();

    const {
        mutate: sendOptMutatation,
        isLoading: otpIsSending,
        status: sendOtpMutationCurrentStatus
    } = useMutation({
        mutationKey: ["data-protection-send-otp"],
        mutationFn: async () => {
            return await axios.post(`${API_BASE_URL}/auth/user/otp-request`, null, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
        },
        onSuccess: () => {
            setOtpSent(true);
        },
        onError: (error) => {
            toast(t("An error occured while sending otp code."), {
                type: "error",
            });
        }
    });

    useEffect(() => {
        if(token && !otpSent && isActiveTab && user?.email){
            sendOptMutatation();
        }
    }, [ 
        token, 
        isActiveTab,
        user?.email
    ]);

    useImperativeHandle(actionsRef, () => {
        return {
            resetOtpForm  :   () => {
                setOtp('');
                setOtpSent(false);
            }
        }
    });

    useEffect(() => {
        if(sendOtpMutationCurrentStatus === 'success'){
            setOtpCountdown(enableOtpResendIn);

            setResendInterval(setInterval(() => {
                setOtpCountdown((currentState) => {
                    return currentState - 1;
                })
            }, 1000));
        }
    }, [sendOtpMutationCurrentStatus])

    useEffect(() => {
        if(otpCountdown <= 0){
            clearInterval(resendInterval);
        }
    }, [ otpCountdown ])

    if(!isActiveTab) return null

    if(!user){
        return (
            <FollowUpForm
                username={username}
                password={password}
                setUsername={setUsername}
                setPassword={setPassword}
                onBackButtonClicked={() => onBackButtonClicked && onBackButtonClicked()}
                onDsrFollowUpDetailsChange={onDsrFollowUpDetailsChange}
                settings={{
                    buttons: {
                        back: {
                            title: "Back"
                        },
                        confirm: {
                            title: "Submit"
                        }
                    },
                    inputs: {
                        email: {
                            required: true
                        }
                    }
                }}
            />
        )
    }   

    if(!user.email) {
        return (
            <UpdateEmail 
                t={t}
                token={token}
                onBackButtonClicked={onBackButtonClicked}
                onUpdateComplete={authCurrentUser}
            />
        )
    }

    if(otpIsSending) {
        return (
            <Alert color="info" role="alert">
                <i className="mdi mdi-check-all me-2"></i>
                <b>
                    {t("Sending OTP...")}
                </b>
                <p>
                    {t(
                        "We are sending an otp code to your email."
                    )}
                </p>
            </Alert>
        )
    }

    return (
        <div>
            <Row className='mb-3'>
                <Col sm="6">
                    <Label className="form-label" htmlFor="otp">
                        {t("OTP Code")}
                    </Label>
                    <div className='d-flex align-items-end'>
                        <InputMask
                            autoFocus
                            mask="9-9-9-9-9-9"
                            className="form-control input-color me-2"
                            value={otp}
                            onChange={(e) => {
                                setOtp(e.target.value)
                            }}
                        >
                            {(inputProps) => (
                                <MaterialInput {...inputProps} disableUnderline />
                            )}
                        </InputMask>
                        <Button
                            disabled={otpCountdown > 0}
                            style={{
                                width: '170px'
                            }}
                            type="button"
                            onClick={() => {
                                sendOptMutatation()
                            }}
                            color="primary"
                        >
                            {otpCountdown > 0 ? (
                                <>{t("Resend OTP in")} {otpCountdown} </>
                            ) : (
                                <>{t("Resend")}</>
                            )}

                        </Button>

                    </div>
                    {otpError && (
                        <FormFeedback invalid className='d-block'>{t(otpError)}</FormFeedback>
                    )}
                </Col>
                <Col sm="6" className='align-content-end'>
                    {
                        otpCountdown > 0
                            ?
                            <p className='ms-3 align-items-end text-primary'>
                                {
                                    t("An OTP code has been sent to your registered email address. Please enter the code.")
                                }
                            </p>
                            :
                            <></>
                    }
                </Col>
            </Row>

            <Row>
                <Col sm="12" className="d-flex justify-content-between align-items-center col-sm-12">
                    <Button
                        onClick={() => onBackButtonClicked && onBackButtonClicked()}
                        color="btn btn-outline-light"
                        type="button">
                        {t("Back")}
                    </Button>

                    <Button
                        onClick={() => {
                            const val = otp.replaceAll("-", "").replaceAll("_", "");
                            if (val.length !== 6) {
                                toast(t('This field is invalid'), {
                                    type: "error",
                                });
                            }
                            else {
                                onUserFilledOtpInput && onUserFilledOtpInput(val);
                            }
                        }}
                        color="btn btn-primary"
                        type="button">
                        {t('Send')}
                    </Button>
                </Col>
            </Row>
        </div>
    )
}

const mapStatetoProps = (state) => {
	const { token, user } = state.Login;
	
	return {
		token,
		user
	};
};

const mapDispachToProps = (dispatch) => {
    return {
        authCurrentUser: () => dispatch(authCurrentUser())
    }
}

export default withNamespaces()(
	connect(mapStatetoProps, mapDispachToProps)(
		FollowUp
	)
);